// Contrats — briques partagées : puce statut, piste de progression,
// carte contrat, rail de rappels, échéancier CDD. Port of the proto's
// ct-parts.jsx + CtBand (ct-shell.jsx).
import { useState } from 'react';
import {
  CG_MONTHS, CgCount, cgAvColor, cgD, cgFmtDate, cgFmtLong, cgInitials, cgIso, entityById,
} from '../conges/common.js';
import {
  CT_ST, ctActive, ctDays, ctDur, ctPct, ctStatus,
  type ContractRow, type ReminderItem,
} from './common.js';

export function CtChip({ p, today }: { p: ContractRow; today: string }) {
  const [lbl, cls] = CT_ST[ctStatus(p, today)] ?? CT_ST.encours!;
  return <span className={'ct-chip ' + cls}>{lbl}</span>;
}

export function CtTrack({ p, today }: { p: ContractRow; today: string }) {
  const start = p.start ?? p.end;
  const done = !ctActive(p);
  const pos = done ? 100 : ctPct(start, p.end, today);
  const tone = done ? '' : pos >= 95 ? 'bad' : pos >= 84 ? 'warn' : '';
  return (
    <div className="ct-prog">
      <div className={'ct-track ' + tone}>
        <div className="ct-track-f" style={{ width: pos + '%' }} />
        {p.milestones.map((ms) => {
          const hit = ms.state && (ms.state.status === 'sent' || ms.state.status === 'done');
          const pending = !hit && ms.date <= today && !done;
          return (
            <i key={ms.code} className={'ct-mark' + (hit ? ' hit' : pending ? ' pending' : '')}
              style={{ left: ctPct(start, p.end, ms.date) + '%' }}
              title={`${ms.code.toUpperCase()} · ${cgFmtLong(ms.date)}`} />
          );
        })}
        {!done && <span className="ct-now" style={{ left: pos + '%' }} />}
      </div>
      <div className="ct-scale">
        <span>{cgFmtDate(start)} <b>début</b></span>
        <span>
          {p.kind === 'essai'
            ? `${p.months}${p.renewMonths ? ` + ${p.renewMonths}` : ''} mois d'essai`
            : `CDD ${p.start ? ctDur(ctDays(p.start, p.end)) : ''}${p.renewals ? ` · ${p.renewals} renouv.` : ''}`}
        </span>
        <span><b>terme</b> {cgFmtDate(p.end)}</span>
      </div>
    </div>
  );
}

export function CtCard({ p, today, i, onOpen }: { p: ContractRow; today: string; i: number; onOpen: () => void }) {
  const dl = ctDays(today, p.end);
  const st = ctStatus(p, today);
  const done = !ctActive(p);
  const tone = done ? 'green' : dl < 0 ? 'bad' : dl <= 30 ? 'warn' : '';
  const ent = entityById(p.employee.entityId);
  return (
    <div className={'ct-card' + (done ? ' done' : '') + (st === 'depasse' || st === 'critique' ? ' alert' : '')}
      style={{ animationDelay: `${i * 40}ms` }} onClick={onOpen}>
      <div className="ct-who">
        <span className="gm-av" style={{ background: cgAvColor(p.employee.id) }}>{cgInitials(p.employee.name)}</span>
        <div className="ct-who-m">
          <b>{p.employee.name}</b>
          <i>{p.employee.department ?? p.employee.code}</i>
          <span className="ct-ent">{ent && <i style={{ background: ent.color }} />}{ent?.short ?? p.employee.entityId}</span>
        </div>
      </div>
      <CtTrack p={p} today={today} />
      <div className="ct-right">
        {done
          ? <span className="ct-cd green">—</span>
          : <span className={'ct-cd ' + tone}><CgCount value={Math.abs(dl)} dec={0} /><i>{dl < 0 ? 'j de retard' : 'j restants'}</i></span>}
        <CtChip p={p} today={today} />
        {p.kind === 'essai' && p.renewMonths != null && ctActive(p) && <span className="ct-chip info">renouvelé · nouveau terme</span>}
        <span className="ct-sub">{done ? cgFmtLong(p.outcomeAt ?? p.end) : `Terme le ${cgFmtLong(p.end)}`}</span>
      </div>
    </div>
  );
}

const CT_TONE: Record<string, [string, string, string]> = {
  info: ['var(--infos)', 'var(--infoc)', '◷'],
  warn: ['var(--warns)', 'var(--warnc)', '⚑'],
  bad: ['var(--bads)', 'var(--badc)', '⚠'],
};

export function CtRemRow({ r, busy, onSend, onDone, onSnooze, onOpen }: {
  r: ReminderItem;
  busy: boolean;
  onSend: () => void;
  onDone: () => void;
  onSnooze: () => void;
  onOpen: () => void;
}) {
  const [bg, fg, ic] = CT_TONE[r.meta.tone]!;
  const [gone, setGone] = useState(false);
  const fire = (fn: () => void) => { setGone(true); setTimeout(fn, 300); };
  return (
    <div className={'ct-rem' + (gone ? ' gone' : '')}>
      <span className="ct-rem-ic" style={{ background: bg, color: fg }}>{ic}</span>
      <div className="ct-rem-m" onClick={onOpen} style={{ cursor: 'pointer' }}>
        <b>{r.p.employee.name} <em>— {r.meta.lbl}</em></b>
        <i>{r.p.kind === 'essai' ? "Période d'essai" : 'CDD'} · {r.meta.sub} · échéance du {cgFmtLong(r.date)} · destinataires : {r.meta.to}</i>
      </div>
      <span className={'ct-late' + (r.late <= 7 ? ' ok' : '')}>{r.late === 0 ? "aujourd'hui" : `J+${r.late}`}</span>
      <div className="ct-rem-act">
        <button className="ct-btn pri" disabled={busy} onClick={() => fire(onSend)}>Envoyer le rappel</button>
        <button className="ct-btn ok" disabled={busy} onClick={() => fire(onDone)}>Traité</button>
        <button className="ct-btn" disabled={busy} onClick={() => fire(onSnooze)}>+7 j</button>
      </div>
    </div>
  );
}

export function CtRail({ due, kindLabel, busy, onSend, onDone, onSnooze, onOpen, onAll }: {
  due: ReminderItem[];
  kindLabel: string;
  busy: boolean;
  onSend: (r: ReminderItem) => void;
  onDone: (r: ReminderItem) => void;
  onSnooze: (r: ReminderItem) => void;
  onOpen: (p: ContractRow) => void;
  onAll: () => void;
}) {
  if (!due.length) {
    return (
      <div className="ct-empty" style={{ marginBottom: 18 }}>
        <b>Aucun rappel en attente</b>
        Toutes les échéances {kindLabel} sont traitées. Le prochain rappel partira automatiquement à la date prévue.
      </div>
    );
  }
  return (
    <div className="ct-rail">
      <div className="ct-rail-h">
        <b>{due.length} rappel{due.length > 1 ? 's' : ''} à traiter</b>
        <i>envoyés à la RH et à la direction aux jalons mi-parcours, J-30 et J-7</i>
        <button className="ct-btn pri ct-allbtn" disabled={busy} onClick={onAll}>Tout envoyer</button>
      </div>
      {due.map((r) => (
        <CtRemRow key={r.key} r={r} busy={busy} onOpen={() => onOpen(r.p)}
          onSend={() => onSend(r)} onDone={() => onDone(r)} onSnooze={() => onSnooze(r)} />
      ))}
    </div>
  );
}

// Échéancier des 6 prochains mois (CDD tab).
export function CtBand({ list, today, onOpen }: { list: ContractRow[]; today: string; onOpen: (p: ContractRow) => void }) {
  const m0 = cgD(today); m0.setUTCDate(1);
  const months = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(m0); d.setUTCMonth(d.getUTCMonth() + i); return d;
  });
  const from = cgIso(months[0]!);
  const toD = new Date(months[6]!); toD.setUTCMonth(toD.getUTCMonth() + 1);
  const to = cgIso(toD);
  const span = ctDays(from, to);
  const pos = (iso: string) => (ctDays(from, iso) / span) * 100;
  const inRange = list.filter((p) => ctActive(p) && p.end >= from && p.end < to);
  const rows = [...inRange].sort((a, b) => (a.end < b.end ? -1 : 1)).map((p, i) => ({ p, row: i % 3 }));
  return (
    <div className="ct-band">
      <div className="ct-band-h">
        <b>Échéancier des 6 prochains mois</b>
        <i>{inRange.length} contrat{inRange.length > 1 ? 's' : ''} arrivant à terme · {list.filter((p) => ctActive(p) && p.end >= to).length} au-delà</i>
      </div>
      <div className="ct-band-grid">
        {months.map((d, i) => (
          <div key={i} className={'ct-bmonth' + (i === 0 ? ' cur' : '')} style={{ left: (i / 7) * 100 + '%', width: 100 / 7 + '%' }}>
            <span>{CG_MONTHS[d.getUTCMonth()]!.slice(0, 4)}.</span>
          </div>
        ))}
        <div className="ct-bline" style={{ left: pos(today) + '%', bottom: 22 }}><b>aujourd'hui</b></div>
        {rows.map(({ p, row }) => {
          const dl = ctDays(today, p.end);
          const c = dl <= 7 ? 'var(--bad)' : dl <= 30 ? 'var(--warn)' : 'var(--shf)';
          const first = p.employee.name.trim().split(/\s+/);
          return (
            <div key={p.employee.id} className="ct-bchip" style={{ left: pos(p.end) + '%', top: 10 + row * 36 }} onClick={() => onOpen(p)}>
              <span className="lbl" style={{ borderColor: c, color: c }}>{first[0]} {first[1]?.[0] ?? ''}. · {dl} j</span>
              <span className="dot" style={{ background: c }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
